import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import { applyPageFilters, sortComparator, getRowIndex } from './helpers';

// Sort a fresh array with the chat comparator. Never sort `allConversations`
// in place: Array.prototype.sort mutates, and mutating the reactive store array
// from inside a getter re-fires that array's Vue 2 dependency on every access —
// the getter invalidates its own subscribers, spinning a full O(n) re-render
// every animation frame under live updates (the conversation-list freeze).
// Sorting a copy keeps these getters side-effect free so they only recompute
// when the set or sort key actually changes. `input` from .filter() is already
// a fresh (non-reactive) array, so it is sorted directly; a stored array must
// be spread first.
const sortConversations = (input, sortKey, { copy = false } = {}) => {
  const list = copy ? [...input] : input;
  return list.sort((a, b) => sortComparator(a, b, sortKey));
};

const getters = {
  getAllConversations: ({ allConversations, chatSortFilter: sortKey }) => {
    return sortConversations(allConversations, sortKey, { copy: true });
  },
  getConversation: _state => {
    return _state.currentConversation || {};
  },
  getConversationId: _state => {
    return _state.currentConversationId || null;
  },
  getSelectedChat: _state => {
    return _state.currentConversation || {};
  },
  getSelectedChatAttachments: ({ currentConversationId, attachments }) => {
    return attachments[currentConversationId] || [];
  },
  getChatListFilters: ({ conversationFilters }) => conversationFilters,
  getLastEmailInSelectedChat: (_stage, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { messages = [] } = selectedChat;

    const message = [...messages]
      .reverse()
      .find(
        m =>
          m.message_type === MESSAGE_TYPE.INCOMING ||
          m.message_type === MESSAGE_TYPE.OUTGOING
      );

    return message;
  },
  // Last INCOMING email in the conversation. Used to prefill the reply
  // recipients (To/Cc/Bcc) from the customer's own email, which is what carries
  // the Cc/Bcc list. Restricting the source to incoming messages prevents a
  // trailing OUTGOING message (an automated reply, an article suggestion, or an
  // AI answer) — none of which carry a Cc — from becoming the recipient source
  // and blanking the field.
  getLastIncomingEmailInSelectedChat: (_stage, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { messages = [] } = selectedChat;

    return [...messages]
      .reverse()
      .find(m => m.message_type === MESSAGE_TYPE.INCOMING);
  },
  getMineChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUserID = rootGetters.getCurrentUser?.id;

    const chats = _state.allConversations.filter(conversation => {
      const { assignee } = conversation.meta;
      const isAssignedToMe = assignee && assignee.id === currentUserID;
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      const isChatMine = isAssignedToMe && shouldFilter;

      return isChatMine;
    });
    // Display order previously relied on getAllConversations sorting
    // allConversations in place as a side effect. That mutation is gone, so
    // sort the filtered subset here to keep tab order identical.
    return sortConversations(chats, _state.chatSortFilter);
  },
  getAppliedConversationFilters: _state => {
    return _state.appliedFilters;
  },
  getAppliedConversationFiltersQuery: _state => {
    const hasAppliedFilters = _state.appliedFilters.length !== 0;
    return hasAppliedFilters ? filterQueryGenerator(_state.appliedFilters) : [];
  },
  getUnAssignedChats: _state => activeFilters => {
    const chats = _state.allConversations.filter(conversation => {
      const isUnAssigned = !conversation.meta.assignee;
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      return isUnAssigned && shouldFilter;
    });
    return sortConversations(chats, _state.chatSortFilter);
  },
  getAllStatusChats: _state => activeFilters => {
    const chats = _state.allConversations.filter(conversation => {
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      return shouldFilter;
    });
    return sortConversations(chats, _state.chatSortFilter);
  },
  getChatListLoadingStatus: ({ listLoadingStatus }) => listLoadingStatus,
  getAllMessagesLoaded(_state) {
    const chat = _state.currentConversation;

    return !chat || chat.allMessagesLoaded === undefined
      ? false
      : chat.allMessagesLoaded;
  },
  getUnreadCount(_state) {
    const chat = _state.currentConversation;

    if (!chat) return [];
    return chat.messages.filter(
      chatMessage =>
        chatMessage.created_at * 1000 > chat.agent_last_seen_at * 1000 &&
        chatMessage.message_type === 0 &&
        chatMessage.private !== true
    ).length;
  },
  getChatStatusFilter: ({ chatStatusFilter }) => chatStatusFilter,
  getChatSortFilter: ({ chatSortFilter }) => chatSortFilter,
  getSelectedInbox: ({ currentInbox }) => currentInbox,
  getConversationById: _state => conversationId => {
    const index = getRowIndex(_state, Number(conversationId));
    return index === -1 ? undefined : _state.allConversations[index];
  },
  getConversationParticipants: _state => {
    return _state.conversationParticipants;
  },
  getConversationLastSeen: _state => {
    return _state.conversationLastSeen;
  },

  getContextMenuChatId: _state => {
    return _state.contextMenuChatId;
  },

  getSubscribedNonAssignedConversations: _state => {
    return _state.subscribedNonAssignedConversations;
  },

  getAudioTranscription: _state => attachmentId => {
    return _state.audioTranscriptions[attachmentId] || null;
  },
  isAudioTranscriptionVisible: _state => attachmentId => {
    return !!_state.audioTranscriptionDisplay[attachmentId];
  },
};

export default getters;
